import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, TrendingUp, Users, Target, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const metrics = [
  { value: '$172K+', label: 'Total Sales', change: '+6%' },
  { value: '44%', label: 'Conversion Rate Boost', change: 'vs previous' },
  { value: '2.64x', label: 'ROAS', change: 'doubled' },
  { value: '76%', label: 'Ad Spend Reduction', change: 'Facebook' },
];

const affiliateMetrics = [
  { value: '$4,767', label: 'GMV in 28 Days' },
  { value: '417.8K', label: 'Total Views' },
  { value: '35', label: 'New Customers' },
  { value: '6,484', label: 'Store Visitors' },
];

const objectives = [
  'Scale affiliate marketing efforts',
  'Reduce overspending on ads with limited return',
  'Achieve consistency in brand messaging',
  'Implement a streamlined system to vet and manage creators',
  'Increase brand awareness and improve conversion rates',
  'Drive sales and achieve sustained growth',
];

const strategies = [
  {
    title: 'Content Strategy',
    items: [
      'Built an aesthetic, authoritative presence rooted in clear messaging and consumer education',
      'Created short-form video content showcasing symptoms, product explanations, and customer reactions',
      'Published 15-20 posts per month across TikTok, Facebook, LinkedIn, and Instagram',
      'Weekly content testing to identify effective topics and styles',
    ],
  },
  {
    title: 'Affiliate Marketing',
    items: [
      'Leveraged Insense to manage and scale the affiliate marketing program',
      'Vetted thousands of creator applicants through the platform',
      'Tracked content submissions and monitored engagement',
      'Generated over $5,000 in affiliate sales via TikTok Shop in 28 days',
    ],
  },
  {
    title: 'Ads Management',
    items: [
      'Optimized ad spend and placements across platforms',
      'Reduced Facebook ad spend by 76% while doubling ROAS',
      'Data-driven approach to creative and targeting',
      'Continuous testing and refinement of campaigns',
    ],
  },
];

export const CaseStudyAFIL = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="case-study-afil">
      {/* Hero Section */}
      <section className="section-padding bg-forest" data-testid="afil-hero">
        <div className="container-custom">
          <Link 
            to="/work" 
            className="inline-flex items-center gap-2 text-cream/70 hover:text-gold transition-colors mb-8"
            data-testid="back-to-work"
          >
            <ArrowLeft size={20} />
            Back to Work
          </Link>
          
          <motion.div
            className="max-w-4xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-6 block">
              Health & Wellness
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cream leading-[1.1] mb-6">
              Advanced Food{' '}
              <span className="text-gold">Intolerance Labs</span>
            </h1>
            <p className="text-xl text-cream/80 max-w-2xl leading-relaxed">
              A multi-channel marketing strategy that drove $172K+ in sales with a 44% conversion rate boost 
              while reducing ad spend by 76%.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img 
          src="https://images.unsplash.com/photo-1535914254981-b5012eebbd15?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="AFIL Case Study"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/50 to-transparent" />
      </section>

      {/* Key Metrics */}
      <section className="section-padding bg-cream" data-testid="afil-metrics">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Results
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-forest">
              Key Performance Metrics
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {metrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                className="bg-cream-paper border border-forest/10 rounded-2xl p-6 text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`metric-${index}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-gold mb-2">{metric.value}</p>
                <p className="text-forest font-medium mb-1">{metric.label}</p>
                <p className="text-sm text-forest/60">{metric.change}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About the Client */}
      <section className="section-padding bg-forest-dark" data-testid="afil-about">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                The Client
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-cream mb-6">
                About AFIL
              </h2>
              <p className="text-cream/70 leading-relaxed mb-6">
                Advanced Food Intolerance Labs is a health and wellness company offering at-home 
                food intolerance test kits. Their mission is to empower people to take control 
                of their health with simple, science-backed solutions.
              </p>
              <p className="text-cream/70 leading-relaxed">
                Their target audience includes individuals interested in personalized wellness 
                and gut health, seeking convenience, education, and preventative care.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-forest rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-cream mb-6">Objectives</h3>
                <ul className="space-y-4">
                  {objectives.map((objective, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
                      <span className="text-cream/80">{objective}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategy Section */}
      <section className="section-padding bg-cream" data-testid="afil-strategy">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Our Approach
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-forest">
              Strategy & Execution
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {strategies.map((strategy, index) => (
              <motion.div
                key={strategy.title}
                className="bg-cream-paper border border-forest/10 rounded-2xl p-8"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`strategy-${index}`}
              >
                <div className="w-12 h-12 bg-gold rounded-xl flex items-center justify-center mb-6">
                  {index === 0 && <Users className="text-forest-dark" size={24} />}
                  {index === 1 && <TrendingUp className="text-forest-dark" size={24} />}
                  {index === 2 && <Target className="text-forest-dark" size={24} />}
                </div>
                <h3 className="text-xl font-semibold text-forest mb-4">{strategy.title}</h3>
                <ul className="space-y-3">
                  {strategy.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-forest/70 text-sm">
                      <span className="text-gold mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliate Metrics */}
      <section className="section-padding bg-forest" data-testid="afil-affiliate">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
              Affiliate Marketing
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-cream">
              TikTok Shop Performance (28 Days)
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {affiliateMetrics.map((metric, index) => (
              <motion.div
                key={metric.label}
                className="bg-forest-dark/50 backdrop-blur rounded-2xl p-6 text-center border border-cream/10"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`affiliate-metric-${index}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-gold mb-2">{metric.value}</p>
                <p className="text-cream/70">{metric.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Summary */}
      <section className="section-padding bg-cream-paper" data-testid="afil-results">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold font-medium tracking-widest uppercase text-sm mb-4 block">
                The Impact
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-forest mb-8">
                Transformative Results
              </h2>
              <p className="text-lg text-forest/70 leading-relaxed mb-8">
                Our multi-channel approach built brand awareness, increased conversions, and positioned 
                AFIL for sustained growth. By leveraging data-driven strategies across content, 
                affiliate marketing, and paid advertising, we were able to significantly reduce 
                wasted ad spend while dramatically improving conversion rates and overall sales.
              </p>
              <div className="inline-block bg-gold/20 rounded-2xl p-8">
                <p className="text-2xl font-bold text-forest">
                  "The multi-channel approach built brand awareness, increased conversions, 
                  and positioned AFIL for sustained growth."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gold" data-testid="afil-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-forest-dark mb-6">
              Ready for Similar Results?
            </h2>
            <p className="text-xl text-forest-dark/70 mb-10 max-w-2xl mx-auto">
              Let's discuss how we can help your brand achieve measurable growth.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="afil-cta-contact">
                <Button className="bg-forest text-cream hover:bg-forest-dark rounded-full px-10 py-6 text-lg font-semibold">
                  Start Your Project
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/work/mommas-voices" data-testid="afil-cta-next">
                <Button variant="outline" className="border-2 border-forest-dark text-forest-dark hover:bg-forest-dark hover:text-cream rounded-full px-10 py-6 text-lg">
                  Next Case Study
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyAFIL;
